import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { BACKEND_URL } from "../lib/api";
import { useTheme } from "../ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import PWAInstallButton from "../components/PWAInstallButton";
import { ImageUploadCard } from "../components/ImageUploadCard";
import { PlayerPhoto } from "../components/MediaImages";
import { requestPermissionAndSubscribe } from "../lib/pushManager";
import { UserCircle, Bell, Mail, Smartphone, Shield, Save, Send, Lock, AlertTriangle, Eye, EyeOff, Calendar, Copy, RefreshCw, ExternalLink, Sun, Moon, Edit, Package } from "lucide-react";

// Iter-70: editable profile fields (the rest — name, email, section, role,
// committee/treasurer — stay admin-managed). Each row defines the field key
// passed to PUT /players/me/profile + the input type / label.
const EDITABLE_FIELDS = [
  { key: "phone", label: "Phone", type: "text", placeholder: "07123 456789" },
  { key: "address", label: "Address", type: "textarea", placeholder: "Street, city, postcode" },
  { key: "jacket_size", label: "Jacket size", type: "text", placeholder: "e.g. 40R" },
  { key: "shirt_size", label: "Shirt size", type: "text", placeholder: "e.g. M" },
  { key: "emergency_contact_name", label: "Emergency contact name", type: "text", placeholder: "Full name" },
  { key: "emergency_contact_phone", label: "Emergency contact phone", type: "text", placeholder: "07123 456789" },
  { key: "emergency_contact_relationship", label: "Emergency contact relationship", type: "text", placeholder: "e.g. Spouse, Parent" },
  { key: "medical_info", label: "Medical requirements", type: "textarea", placeholder: "Allergies, conditions, medications" },
  { key: "dietary_info", label: "Dietary requirements", type: "textarea", placeholder: "Vegetarian, allergies, etc." },
];

const Profile = () => {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [myPlayerId, setMyPlayerId] = useState(null);
  const [photoBust, setPhotoBust] = useState(Date.now());
  const [hasPhoto, setHasPhoto] = useState(false);

  // Iter-70: self-edit profile state + assigned inventory items.
  const [myPlayer, setMyPlayer] = useState(null);          // full player record
  const [profileForm, setProfileForm] = useState({});      // editable fields snapshot
  const [savingProfile, setSavingProfile] = useState(false);
  const [myAssignedItems, setMyAssignedItems] = useState([]);

  useEffect(() => {
    // Pull player record + assigned items in parallel.
    (async () => {
      try {
        const [profileRes, itemsRes] = await Promise.all([
          apiClient.get("/players/me/profile"),
          apiClient.get("/inventory/my-assignments"),
        ]);
        const p = profileRes.data?.player || null;
        setMyPlayer(p);
        if (p) {
          // Snapshot the editable subset so the form has initial values
          const init = {};
          for (const f of EDITABLE_FIELDS) init[f.key] = p[f.key] || "";
          setProfileForm(init);
        }
        setMyAssignedItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      } catch {
        // silent — profile may simply not exist yet for a brand-new admin
      }
    })();
  }, [user?.id]);

  const handleProfileFieldChange = (key, value) => {
    setProfileForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveProfile = async (e) => {
    e?.preventDefault();
    setSavingProfile(true);
    try {
      // Only send fields the user has actually touched (compare against myPlayer)
      const diff = {};
      for (const f of EDITABLE_FIELDS) {
        const cur = profileForm[f.key] ?? "";
        const orig = myPlayer?.[f.key] ?? "";
        if (cur !== orig) diff[f.key] = cur;
      }
      if (Object.keys(diff).length === 0) {
        toast.info("No changes to save");
        return;
      }
      const res = await apiClient.put("/players/me/profile", diff);
      setMyPlayer(res.data);
      toast.success("Profile updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const res = await apiClient.get("/players");
        const me = (res.data || []).find(
          (p) => p.user_id === user.id || (p.email || "").toLowerCase() === (user.email || "").toLowerCase()
        );
        setMyPlayerId(me?.id || null);
      } catch {
        setMyPlayerId(null);
      }
    })();
  }, [user?.id, user?.band_id]);

  useEffect(() => {
    if (!myPlayerId) return;
    const token = localStorage.getItem("token");
    fetch(`${BACKEND_URL}/api/players/${myPlayerId}/photo`, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => setHasPhoto(r.ok))
      .catch(() => setHasPhoto(false));
  }, [myPlayerId, photoBust]);

  useEffect(() => {
    // Check if push notifications are supported
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true);
      checkPushSubscription();
    }
  }, []);

  const checkPushSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushEnabled(!!subscription);
    } catch (error) {
      console.error("Error checking push subscription:", error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success("Password changed successfully!");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      // Refresh user data to update temp_password flag
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const togglePushNotifications = async () => {
    if (!pushSupported) {
      toast.error("Push notifications are not supported in this browser");
      return;
    }

    setLoading(true);
    try {
      if (pushEnabled) {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          setPushEnabled(false);
          toast.success("Push notifications disabled");
        }
      } else {
        // Use the shared helper so this matches dashboard banner + auto-subscribe
        const status = await requestPermissionAndSubscribe();
        if (status === "granted") {
          setPushEnabled(true);
          toast.success("Push notifications enabled");
        } else if (status === "denied") {
          toast.error("Notification permission denied");
        } else if (status === "unsupported") {
          toast.error("Push notifications are not supported in this browser");
        } else {
          toast.error("Failed to enable push notifications");
        }
      }
    } catch (error) {
      console.error("Error toggling push notifications:", error);
      toast.error("Failed to update push notification settings");
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    if (!pushEnabled) {
      toast.error("Please enable push notifications first");
      return;
    }
    
    setTestingPush(true);
    try {
      await apiClient.post("/push/test");
      toast.success("Test notification sent! Check your browser notifications.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    } finally {
      setTestingPush(false);
    }
  };

  const generateCalendarLink = async () => {
    setLoadingCalendar(true);
    try {
      const res = await apiClient.post("/calendar/subscribe");
      setCalendarUrl(res.data);  // Store the full response object
      toast.success("Calendar subscription link generated!");
    } catch (error) {
      toast.error("Failed to generate calendar link");
    } finally {
      setLoadingCalendar(false);
    }
  };

  const copyCalendarUrl = () => {
    if (calendarUrl) {
      const url = calendarUrl.subscribe_url || calendarUrl;
      navigator.clipboard.writeText(url);
      toast.success("Calendar URL copied to clipboard!");
    }
  };

  const revokeCalendarLink = async () => {
    try {
      await apiClient.delete("/calendar/subscribe");
      setCalendarUrl(null);
      toast.success("Calendar subscription revoked");
    } catch (error) {
      toast.error("Failed to revoke calendar link");
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Profile</h1>
        <p className="text-muted-foreground mt-1">Manage your account settings</p>
      </div>

      {/* Temporary Password Warning */}
      {user?.temp_password && (
        <Card className="border-orange-500/50 bg-orange-500/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-orange-500">Please Change Your Password</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  You're using a temporary password. For security, please change it below.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* iter-56: when on a temp password, hide the rest of the profile and force
          focus onto the change-password form below. */}
      {!user?.temp_password && (
      <>
      {/* Profile Card */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-primary" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <PlayerPhoto
              playerId={myPlayerId}
              bust={photoBust}
              className="w-20 h-20 rounded-full"
              fallback={
                <div className="w-20 h-20 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <UserCircle className="w-10 h-10 text-primary" />
                </div>
              }
            />
            <div className="flex-1 space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Full Name</Label>
                <p className="text-lg font-medium">{user?.name}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Email Address</Label>
                <p className="text-base sm:text-lg font-medium flex items-center gap-2 min-w-0">
                  <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="break-all">{user?.email}</span>
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Role</Label>
                <div className="mt-1">
                  <Badge 
                    variant="outline" 
                    className={user?.role === "admin" 
                      ? "border-primary/30 text-primary" 
                      : "border-blue-400/30 text-blue-400"
                    }
                  >
                    <Shield className="w-3 h-3 mr-1" />
                    {user?.role === "admin" ? "Administrator" : "Player"}
                  </Badge>
                </div>
              </div>
              {myPlayerId && (
                <div className="pt-2 border-t border-white/5">
                  <Label className="text-xs text-muted-foreground mb-3 block">Profile Photo</Label>
                  <ImageUploadCard
                    postPath={`/players/${myPlayerId}/photo`}
                    deletePath={`/players/${myPlayerId}/photo`}
                    currentImage={null}
                    hasImage={hasPhoto}
                    onChange={(b) => setPhotoBust(b || Date.now())}
                    testIdPrefix="profile-photo"
                    helpText="Visible to other members of your band. PNG / JPEG / WebP, max 5MB."
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Iter-70: Edit My Profile (members can update their own contact info,
          sizes, emergency contact and medical/dietary requirements). They
          cannot change their primary/secondary section, player type, or
          committee/treasurer flags — those stay admin-only. */}
      {myPlayer && (
        <Card className="glass-card" data-testid="edit-my-profile-card">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Edit My Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-5">
              You can update your contact details, sizes, emergency contact, and any medical/dietary requirements.
              Your section assignments are managed by your band admin.
            </p>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {EDITABLE_FIELDS.filter((f) => f.type === "text").map((f) => (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={`pf-${f.key}`} className="text-xs text-muted-foreground">{f.label}</Label>
                    <Input
                      id={`pf-${f.key}`}
                      value={profileForm[f.key] || ""}
                      placeholder={f.placeholder}
                      onChange={(e) => handleProfileFieldChange(f.key, e.target.value)}
                      className="bg-secondary/50"
                      data-testid={`profile-input-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              {EDITABLE_FIELDS.filter((f) => f.type === "textarea").map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label htmlFor={`pf-${f.key}`} className="text-xs text-muted-foreground">{f.label}</Label>
                  <Textarea
                    id={`pf-${f.key}`}
                    value={profileForm[f.key] || ""}
                    placeholder={f.placeholder}
                    onChange={(e) => handleProfileFieldChange(f.key, e.target.value)}
                    rows={f.key === "address" ? 2 : 3}
                    className="bg-secondary/50 text-sm"
                    data-testid={`profile-input-${f.key}`}
                  />
                </div>
              ))}
              <div className="pt-2">
                <Button type="submit" disabled={savingProfile} data-testid="save-my-profile-btn">
                  {savingProfile ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save changes</>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Iter-70: Items issued to me — read-only inventory list scoped to
          the logged-in user. Members get visibility on what they have on loan
          without needing admin access. */}
      {myPlayer && (
        <Card className="glass-card" data-testid="my-assigned-items-card">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Items Issued to Me
              <Badge variant="outline" className="ml-2 text-[10px]">{myAssignedItems.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {myAssignedItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                You don&apos;t currently have any items on loan from the band.
              </p>
            ) : (
              <ul className="divide-y divide-white/5">
                {myAssignedItems.map((it) => (
                  <li key={it.id} className="py-3 flex items-start justify-between gap-3" data-testid={`my-item-${it.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium">{it.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span className="capitalize">{it.category}</span>
                        {it.subcategory && <span>· {it.subcategory}</span>}
                        {it.asset_tag && <span className="font-mono">· {it.asset_tag}</span>}
                        {it.serial_number && <span className="font-mono">· SN: {it.serial_number}</span>}
                      </div>
                    </div>
                    {it.condition && (
                      <Badge variant="outline" className="capitalize flex-shrink-0">
                        {String(it.condition).replace(/_/g, " ")}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-white/5">
              To return or transfer an item, please contact your band admin.
            </p>
          </CardContent>
        </Card>
      )}
      </>
      )}

      {/* Change Password */}
      <Card className={`glass-card ${user?.temp_password ? 'border-orange-500/30' : ''}`}>
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            Change Password
            {user?.temp_password && (
              <Badge className="bg-orange-500/20 text-orange-500 ml-2">Action Required</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="bg-secondary/50 pr-10"
                  placeholder="Enter your current password"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="bg-secondary/50 pr-10"
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className="bg-secondary/50"
                placeholder="Confirm new password"
                required
                data-testid="confirm-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={changingPassword}
              className="bg-primary text-primary-foreground"
              data-testid="change-password-btn"
            >
              {changingPassword ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {!user?.temp_password && (
      <>
      {/* Appearance — iter-66: Light/Dark mode toggle */}
      <Card className="glass-card" data-testid="appearance-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            {theme === "light" ? (
              <Sun className="w-5 h-5 text-primary" />
            ) : (
              <Moon className="w-5 h-5 text-primary" />
            )}
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                {theme === "light" ? (
                  <Sun className="w-5 h-5 text-primary" />
                ) : (
                  <Moon className="w-5 h-5 text-primary" />
                )}
              </div>
              <div>
                <p className="font-medium">{theme === "light" ? "Light mode" : "Dark mode"}</p>
                <p className="text-sm text-muted-foreground">
                  Switch between dark and light themes. Your choice is saved on this device.
                </p>
              </div>
            </div>
            <Switch
              checked={theme === "light"}
              onCheckedChange={toggleTheme}
              data-testid="theme-toggle"
              aria-label="Toggle light mode"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Notification Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Email Notifications */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Receive event reminders and announcements via email
                </p>
              </div>
            </div>
            <Badge className="bg-green-500/20 text-green-400">Active</Badge>
          </div>

          {/* Push Notifications */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium">Push Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Get instant alerts in your browser
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {pushEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendTestNotification}
                  disabled={testingPush}
                  className="border-primary/30 text-primary hover:bg-primary/10"
                  data-testid="test-push-btn"
                >
                  <Send className="w-4 h-4 mr-1" />
                  {testingPush ? 'Sending...' : 'Test'}
                </Button>
              )}
              {pushSupported ? (
                <Switch
                  checked={pushEnabled}
                  onCheckedChange={togglePushNotifications}
                  disabled={loading}
                  data-testid="push-toggle"
                />
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  Not Supported
                </Badge>
              )}
            </div>
          </div>

          {!pushSupported && (
            <p className="text-xs text-muted-foreground text-center">
              Push notifications require a modern browser with service worker support
            </p>
          )}
        </CardContent>
      </Card>

      {/* Calendar Sync */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Calendar Sync
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Subscribe to your events in your favorite calendar app (Google Calendar, Apple Calendar, Outlook).
            Events you&apos;ve responded &quot;Yes&quot; or &quot;Maybe&quot; to will automatically sync.
          </p>
          
          {calendarUrl ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Calendar URL (copy and paste into your calendar app)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={calendarUrl.subscribe_url || calendarUrl}
                    readOnly
                    className="bg-secondary/50 text-sm font-mono"
                    data-testid="calendar-url-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyCalendarUrl}
                    className="border-primary/30"
                    data-testid="copy-calendar-url-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const url = calendarUrl.subscribe_url || calendarUrl;
                    window.open(`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(url)}`, '_blank');
                  }}
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                  data-testid="add-to-google-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Add to Google Calendar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={revokeCalendarLink}
                  className="text-red-400 hover:bg-red-500/10"
                  data-testid="revoke-calendar-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate Link
                </Button>
              </div>
              
              <div className="text-xs text-muted-foreground bg-secondary/30 p-3 rounded-lg">
                <p className="font-medium text-foreground mb-2">How to add manually:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li><strong>Google Calendar:</strong> Settings → Add calendar → From URL → paste the URL</li>
                  <li><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste the URL</li>
                  <li><strong>Outlook:</strong> Add calendar → Subscribe from web → paste the URL</li>
                </ol>
              </div>
            </div>
          ) : (
            <Button
              onClick={generateCalendarLink}
              disabled={loadingCalendar}
              className="bg-primary text-primary-foreground"
              data-testid="generate-calendar-btn"
            >
              {loadingCalendar ? (
                "Generating..."
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Generate Calendar Link
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Account Actions */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-heading text-lg">Account Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Button 
            variant="destructive" 
            onClick={logout}
            data-testid="profile-logout-btn"
          >
            Sign Out
          </Button>
        </CardContent>
      </Card>

      {/* Link to GDPR / data rights page */}
      <Card className="glass-card">
        <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-lg">Install Vivo as an app</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Install Vivo on your phone or desktop for faster access and offline support.
            </p>
          </div>
          <PWAInstallButton />
        </CardContent>
      </Card>

      {/* Link to GDPR / data rights page */}
      <Card className="glass-card">
        <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-lg">Data &amp; Privacy</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Download or delete your data, update consent preferences.
            </p>
          </div>
          <Link to="/my-data">
            <Button variant="outline" data-testid="profile-my-data-link">Manage</Button>
          </Link>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

export default Profile;
